import { combineReducers } from 'redux';
import { handleActions } from 'redux-actions';

import { utcDateFormatter } from 'common/services/date';
import actions from '../actions';

const {
  REWARDS_REQUEST,
  REWARDS_FAILURE,
  REWARDS_SUCCESS,
  REWARD_DETAILS_REQUEST,
  REWARD_DETAILS_FAILURE,
  REWARD_DETAILS_SUCCESS,
} = actions;

const defaults = {
  loading: false,
  error: null,
};

const formatCurrency = (amount, currency) => {
  const numAmount = parseFloat(amount);
  return `${numAmount.toFixed(2)} ${currency}`;
};

const formatPercentage = (decimal) => {
  const percentage = parseFloat(decimal) * 100;
  return `${
    percentage % 1 === 0 ? percentage.toFixed(0) : percentage.toFixed(1)
  }%`;
};

const formatXmn = (amount) => {
  const numAmount = parseFloat(amount);
  return `${numAmount.toFixed(6)} XMN`;
};

const calculateProgress = (startDate, unlockingDate) => {
  const start = new Date(startDate);
  const unlock = new Date(unlockingDate);
  const now = new Date();
  const totalDuration = unlock.getTime() - start.getTime();
  const elapsed = now.getTime() - start.getTime();
  return Math.max(0, Math.min(1, elapsed / totalDuration));
};

const calculateDaysLeft = (unlockingDate) => {
  const unlock = new Date(unlockingDate);
  const now = new Date();
  const diffTime = unlock.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
};

const formatDate = (dateString) => {
  const displayUtcDate = utcDateFormatter(dateString);
  return displayUtcDate('dd/MM/yyyy');
};

const formatPeriod = (startDate) => {
  const date = new Date(startDate);
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  return `${monthNames[date.getMonth()]} ${date.getFullYear()} rewards`;
};

const transformRewardsData = (payload) => {
  if (!payload) return null;

  const data = payload.response || payload;
  const referenceCurrency = data.reference_currency || 'EUR';

  const balance = {
    totalEur: formatCurrency(
      data.total_xmn_balance_reference || '0',
      referenceCurrency
    ),
    totalXmn: formatXmn(data.total_xmn_balance || '0'),
    locked: {
      xmn: formatXmn(data.total_locked_xmn || '0'),
      eur: formatCurrency(
        data.total_locked_xmn_reference || '0',
        referenceCurrency
      ),
    },
    unlocked: {
      xmn: formatXmn(data.total_unlocked_xmn || '0'),
      eur: formatCurrency(
        data.total_unlocked_xmn_reference || '0',
        referenceCurrency
      ),
    },
  };

  const nextRewardRate = {
    rate: formatPercentage(data.merchant_percentage || '0'),
    description:
      'A variable percentage of your settlement costs is used to purchase XMN tokens that you can withdraw after a 3 month lock period.',
  };

  const schedule = (data.data || []).map((item, index) => {
    const unlocked = item.unlocked || false;
    const unlockingDate = item.unlocking_date;
    const startDate = item.start_date;
    const progress = calculateProgress(startDate, unlockingDate);
    const daysLeft = calculateDaysLeft(unlockingDate);

    return {
      id: `${index}-${unlockingDate}`,
      amount: formatXmn(item.total_xmn_amount || '0'),
      period: formatPeriod(startDate),
      daysLeft,
      status: unlocked ? 'unlocked' : 'locked',
      startDate: formatDate(startDate),
      unlockDate: formatDate(unlockingDate),
      progress,
      unlockingDate,
      startDateISO: startDate,
    };
  });

  return {
    balance,
    nextRewardRate,
    schedule,
    referenceCurrency,
  };
};

const transformRewardDetails = (payload) => {
  if (!payload) return null;

  const data = payload.response || payload;
  const referenceCurrency = 'EUR';

  return {
    unlockingDate: data.unlocking_date,
    startDate: data.start_date,
    rewards: (data.rewards || []).map((reward) => {
      const displayUtcDate = utcDateFormatter(reward.settlement_date);
      const formattedDate = displayUtcDate('dd MMM yyyy');

      return {
        settlementDate: formattedDate,
        settlementFee: formatCurrency(
          reward.settlement_fee || '0',
          referenceCurrency
        ),
        rate: formatPercentage(reward.reward_percentage || '0'),
        earnedEur: formatCurrency(
          reward.settlement_fee || '0',
          referenceCurrency
        ),
        earnedXmn: formatXmn(reward.xmn_amount || '0'),
      };
    }),
  };
};

const rewardsReducer = handleActions(
  {
    [REWARDS_REQUEST]: () => ({
      ...defaults,
      loading: true,
    }),
    [REWARDS_SUCCESS]: (state, action) => {
      const transformed = transformRewardsData(action.payload);
      return {
        ...defaults,
        data: transformed,
      };
    },
    [REWARDS_FAILURE]: (state, action) => ({
      ...defaults,
      error: action.payload,
    }),
  },
  defaults
);

const rewardDetailsReducer = handleActions(
  {
    [REWARD_DETAILS_REQUEST]: () => ({
      ...defaults,
      loading: true,
    }),
    [REWARD_DETAILS_SUCCESS]: (state, action) => {
      const transformed = transformRewardDetails(action.payload);
      return {
        ...defaults,
        data: transformed,
      };
    },
    [REWARD_DETAILS_FAILURE]: (state, action) => ({
      ...defaults,
      error: action.payload,
    }),
  },
  defaults
);

export default combineReducers({
  overview: rewardsReducer,
  details: rewardDetailsReducer,
});

export const rewardsData = (state) => state.rewards.overview.data;
export const isFetchingRewards = (state) => state.rewards.overview.loading;
export const rewardsError = (state) => state.rewards.overview.error;
export const rewardDetails = (state) => state.rewards.details.data;
export const isFetchingRewardDetails = (state) => state.rewards.details.loading;
export const rewardDetailsError = (state) => state.rewards.details.error;
